import React, {useEffect, useMemo, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {LinhasDaComposicao} from "../../../shared/ui/ComposicaoDaFatura";
import {formatDataIsoBR, formatReais} from "../../../shared/utils/formatters";
import {getBackendErrorMessage} from "../../../shared/utils/httpError";
import {simularVersao} from "../PlanosComerciaisService";
import {plural} from "../planoVersaoUtils";

const SEM_DESCONTO = '';

const respostaDaSimulacao = (response) => response?.data?.data ?? response?.data?.valor ?? null;

// O 404 do próprio gateway (rota que ainda não está lá) não traz o envelope do app; o 404 do app é a versão que não
// existe e tem mensagem. 502 é o cálculo fora do ar.
const indisponivel = (error) => {
    const status = error?.response?.status;
    if (!error?.response || [502, 503, 504].includes(status)) return true;
    return status === 404 && typeof error.response.data?.success !== 'boolean';
};

/*
 * Simulador da versão (BBF-107): a fatura de uma assinatura desta versão, com os opcionais marcados e um desconto,
 * pelo cálculo único da fatura (BBF-24). Nada é gravado. O rascunho precisa estar salvo, porque o cálculo lê a
 * versão da API.
 */
export default function SimuladorCard({versao, modulos, rascunhoSujo}) {
    const opcionais = useMemo(() => (versao.modulos || [])
        .filter((item) => item.situacao === 'OPCIONAL')
        .map((item) => ({...item, nome: item.modulo?.nome || modulos.find((modulo) => modulo.id === item.id_modulo)?.nome || 'Módulo'})), [modulos, versao.modulos]);
    const [marcados, setMarcados] = useState(() => opcionais.map((item) => item.id_modulo));
    const [descontoTipo, setDescontoTipo] = useState(SEM_DESCONTO);
    const [descontoValor, setDescontoValor] = useState('');
    const [resultado, setResultado] = useState({carregando: false, valor: null, erro: '', indisponivel: false});

    useEffect(() => {
        setMarcados(opcionais.map((item) => item.id_modulo));
        setResultado({carregando: false, valor: null, erro: '', indisponivel: false});
    }, [opcionais]);

    const simular = async () => {
        setResultado({carregando: true, valor: null, erro: '', indisponivel: false});
        try {
            const valorDoDesconto = Number(String(descontoValor).replace(',', '.'));
            const response = await simularVersao({
                idPlanoVersao: versao.id,
                opcionais: marcados,
                desconto: descontoTipo && valorDoDesconto > 0 ? {tipo: descontoTipo, valor: valorDoDesconto} : null,
            });
            setResultado({carregando: false, valor: respostaDaSimulacao(response), erro: '', indisponivel: false});
        } catch (error) {
            setResultado(indisponivel(error)
                ? {carregando: false, valor: null, erro: '', indisponivel: true}
                : {carregando: false, valor: null, erro: getBackendErrorMessage(error, 'Não foi possível simular.'), indisponivel: false});
        }
    };

    const alternar = (idModulo) => setMarcados((atual) => (atual.includes(idModulo) ? atual.filter((id) => id !== idModulo) : [...atual, idModulo]));

    return (
        <Paper variant="outlined" sx={{p: {xs: 2, md: 2.5}}} component="section" aria-label="Simulador de fatura">
            <Typography variant="h6" component="h2" fontWeight={700}>Simulador de fatura</Typography>
            <Typography variant="body2" color="text.secondary">
                Uma assinatura desta versão, pelo mesmo cálculo da fatura. Métricas e excedente entram na fase 5.
            </Typography>

            <Box sx={{display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr)', md: 'minmax(240px, 1fr) minmax(0, 2fr)'}, gap: 3, mt: 2}}>
                <Stack spacing={2}>
                    {opcionais.length ? (
                        <Box>
                            <Typography variant="caption" color="text.secondary">Opcionais contratados</Typography>
                            {opcionais.map((item) => (
                                <FormControlLabel key={item.id_modulo} sx={{display: 'flex'}}
                                                  control={<Checkbox size="small" checked={marcados.includes(item.id_modulo)} onChange={() => alternar(item.id_modulo)} />}
                                                  label={<span>{item.nome} <Typography component="span" variant="caption" color="text.secondary">+ {formatReais(item.preco_opcional)}</Typography></span>} />
                            ))}
                        </Box>
                    ) : <Typography variant="body2" color="text.secondary">Esta versão não tem opcionais.</Typography>}
                    <Stack direction="row" spacing={1}>
                        <TextField select size="small" label="Desconto" value={descontoTipo} onChange={(event) => setDescontoTipo(event.target.value)} sx={{minWidth: 150}}>
                            <MenuItem value={SEM_DESCONTO}>Sem desconto</MenuItem>
                            <MenuItem value="PERCENTUAL">Percentual</MenuItem>
                            <MenuItem value="VALOR">Em reais</MenuItem>
                        </TextField>
                        {descontoTipo ? (
                            <TextField size="small" type="number" label="Valor" value={descontoValor} onChange={(event) => setDescontoValor(event.target.value)}
                                       inputProps={{min: 0, step: descontoTipo === 'PERCENTUAL' ? 1 : 10}}
                                       InputProps={descontoTipo === 'PERCENTUAL'
                                           ? {endAdornment: <InputAdornment position="end">%</InputAdornment>}
                                           : {startAdornment: <InputAdornment position="start">R$</InputAdornment>}} />
                        ) : null}
                    </Stack>
                    {versao.dias_trial ? (
                        <Alert severity="info" icon={false}>
                            Trial de {plural(versao.dias_trial, 'dia', 'dias')}: o cartão é pedido na adesão. No fim do trial, a fatura de entrada cobra o proporcional até o fechamento da empresa.
                        </Alert>
                    ) : null}
                    <Box>
                        <Button variant="outlined" onClick={simular} disabled={rascunhoSujo || resultado.carregando}>
                            {resultado.carregando ? 'Simulando…' : 'Simular a fatura'}
                        </Button>
                        {rascunhoSujo ? <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 0.5}}>Salve o rascunho para simular com as mudanças.</Typography> : null}
                    </Box>
                </Stack>

                <Box aria-live="polite">
                    {resultado.carregando ? <CircularProgress size={20} /> : null}
                    {resultado.indisponivel ? (
                        <Alert severity="info">
                            Simulador indisponível no momento. O resto da página funciona normalmente; tente de novo em alguns minutos.
                        </Alert>
                    ) : null}
                    {resultado.erro ? <Alert severity="warning">{resultado.erro}</Alert> : null}
                    {resultado.valor ? (
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                {resultado.valor.emissao ? `Fatura do fechamento de ${formatDataIsoBR(resultado.valor.emissao)}. ` : ''}
                                {resultado.valor.periodo_inicio ? `Mensalidade de ${formatDataIsoBR(resultado.valor.periodo_inicio)} a ${formatDataIsoBR(resultado.valor.periodo_fim)}.` : ''}
                            </Typography>
                            <LinhasDaComposicao linhas={resultado.valor.linhas || []} />
                            <Stack direction="row" justifyContent="space-between" sx={{pt: 1, borderTop: 2, borderColor: 'divider'}}>
                                <Typography fontWeight={700}>Total na fatura</Typography>
                                <Typography fontWeight={700} sx={{fontVariantNumeric: 'tabular-nums'}}>{formatReais(resultado.valor.total)}</Typography>
                            </Stack>
                        </Box>
                    ) : null}
                    {!resultado.carregando && !resultado.valor && !resultado.indisponivel && !resultado.erro ? (
                        <Typography variant="body2" color="text.secondary">Escolha os opcionais e o desconto, e simule.</Typography>
                    ) : null}
                </Box>
            </Box>
        </Paper>
    );
}
